import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiJson, publicApiJson } from "../utils/fetch";
import type {
  BillingSummary,
  CheckoutResponse,
  FinalizeResult,
} from "../types";

export function finalizeTransaction(checkoutSessionId: string) {
  return apiJson<FinalizeResult>(
    `/billing/checkout-sessions/${encodeURIComponent(checkoutSessionId)}/finalize`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function useBillingSummary(orgId: string) {
  return useQuery({
    queryKey: ["orgs", orgId, "billing"],
    queryFn: () => apiJson<BillingSummary>(`/orgs/${orgId}/billing`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

export function useStartCheckout(orgId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<CheckoutResponse>(`/orgs/${orgId}/billing/checkout`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });
}

export function useCancelSubscription(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ ok: boolean }>(`/orgs/${orgId}/billing/cancel`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "billing"] });
    },
  });
}

export interface PortalLinks {
  overview: string;
  update_payment_method: string | null;
  cancel_subscription: string | null;
}

export function useOrgBillingPortal(orgId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<PortalLinks>(`/orgs/${orgId}/billing/portal`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });
}

export interface VerifyPlanResult {
  active: boolean;
  plan: string;
}

export function useVerifyProActive(userId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<VerifyPlanResult>(
        `/users/${userId}/billing/verify-pro-active`,
        { method: "POST", body: JSON.stringify({}) },
      ),
  });
}

export function useSetUserMaxSpend(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (max_spend_cents: number | null) =>
      apiJson<{ ok: boolean }>(
        `/users/${userId}/billing/max-spend`,
        {
          method: "PATCH",
          body: JSON.stringify({ max_spend_cents }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "billing"] });
    },
  });
}

export function useSetOrgMaxSpend(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (max_spend_cents: number | null) =>
      apiJson<{ ok: boolean }>(
        `/orgs/${orgId}/billing/max-spend`,
        {
          method: "PATCH",
          body: JSON.stringify({ max_spend_cents }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "billing"] });
    },
  });
}

export function useVerifyTeamActive(orgId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<VerifyPlanResult>(
        `/orgs/${orgId}/billing/verify-team-active`,
        { method: "POST", body: JSON.stringify({}) },
      ),
  });
}

export function useUserBillingPortal(userId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<PortalLinks>(`/users/${userId}/billing/portal`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });
}

export interface PricePoint {
  id: string;
  amount_cents: number;
  currency: string;
  interval: string | null;
}

export interface PricesSnapshot {
  task_base: PricePoint | null;
  organization: PricePoint | null;
  seat: PricePoint | null;
}

export function usePricing() {
  return useQuery({
    queryKey: ["billing", "prices"],
    queryFn: () => publicApiJson<PricesSnapshot>(`/billing/prices`),
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

// ---------- User billing (Pro plan) ----------

export function useUserBilling(userId: string) {
  return useQuery({
    queryKey: ["users", userId, "billing"],
    queryFn: () => apiJson<BillingSummary>(`/users/${userId}/billing`),
    enabled: Boolean(userId),
    refetchOnWindowFocus: false,
  });
}

export function useStartUserCheckout(userId: string) {
  return useMutation({
    mutationFn: () =>
      apiJson<CheckoutResponse>(`/users/${userId}/billing/checkout`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
  });
}

export function useCancelUserSubscription(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ ok: boolean }>(`/users/${userId}/billing/cancel`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "billing"] });
    },
  });
}

export interface TeamUpgradeResponse extends CheckoutResponse {
  org_id: string;
}

export function useTeamUpgrade(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiJson<TeamUpgradeResponse>(`/users/${userId}/billing/team-upgrade`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}
